/* custom-Image-video-with-text.js
   Injects <img> or <video> based on data-src (or forced by data-type),
   removes video controls, and adaptively sizes the left panel so portrait
   media can be shown fully when possible (auto-height), falling back to
   a clamped height + contain behavior.
*/

(function () {
  function isVideoSrc(src) {
    if (!src || typeof src !== 'string') return false;
    const lower = src.split('?')[0].toLowerCase();
    return /\.(mp4|webm|ogg|mov|m4v)$/.test(lower);
  }

  function initCIVT(rootId = 'civt-root') {
    const root = document.getElementById(rootId);
    if (!root) return;

    // Normalize text alignment variables for the right panel
    normalizeTextAlignment(root);

    const mediaEl = root.querySelector('.media');
    if (!mediaEl) return;
    const src = (mediaEl.getAttribute('data-src') || '').trim();
    const forcedType = (mediaEl.getAttribute('data-type') || '').trim().toLowerCase();

    if (!src) {
      mediaEl.innerHTML = '<div class="fallback">No media provided</div>';
      return;
    }

    const treatAsVideo = forcedType === 'video' || isVideoSrc(src);

    // inject element
    mediaEl.innerHTML = '';
    if (treatAsVideo) {
      const video = document.createElement('video');
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.autoplay = true;
      video.loop = true;
      video.muted = true;      // required for autoplay in many browsers
      video.controls = false;  // IMPORTANT: disable native controls
      video.style.width = '100%';
      video.style.height = '100%';
      video.style.objectFit = 'cover';
      video.style.objectPosition = 'top center';
      video.style.display = 'block';

      const source = document.createElement('source');
      source.src = src;
      const ext = src.split('.').pop().split('?')[0].toLowerCase();
      if (ext === 'mp4') source.type = 'video/mp4';
      else if (ext === 'webm') source.type = 'video/webm';
      else if (ext === 'ogg' || ext === 'ogv') source.type = 'video/ogg';
      video.appendChild(source);
      mediaEl.appendChild(video);

      video.addEventListener('error', function () {
        mediaEl.innerHTML = '<div class="fallback">Could not load video</div>';
      });

      // Try play (some browsers block autoplay despite muted; this is best-effort)
      video.play().catch(() => { /* ignore autoplay error */ });

    } else {
      const img = document.createElement('img');
      img.alt = mediaEl.getAttribute('data-alt') || 'media';
      img.decoding = 'async';
      img.src = src;
      img.style.width = '100%';
      img.style.height = '100%';
      img.style.objectFit = 'cover';
      img.style.objectPosition = 'top center';
      img.style.display = 'block';
      mediaEl.appendChild(img);

      img.addEventListener('error', function () {
        mediaEl.innerHTML = '<div class="fallback">Could not load image</div>';
      });
    }

    // adaptive sizing for both image and video
    runAdaptiveSizer(root, mediaEl);
  }

  function normalizeTextAlignment(root) {
    const computed = getComputedStyle(root);
    const textAlign = computed.getPropertyValue('--text-align').trim() || 'center';
    const textVerticalRaw = computed.getPropertyValue('--text-vertical').trim() || 'flex-start';

    let jc = 'center';
    const tv = textVerticalRaw.replace(/['"]/g, '').toLowerCase();
    if (tv === 'top' || tv === 'flex-start' || tv === 'start') jc = 'flex-start';
    else if (tv === 'center' || tv === 'middle') jc = 'center';
    else if (tv === 'bottom' || tv === 'flex-end' || tv === 'end') jc = 'flex-end';

    let ai = 'center';
    const ta = textAlign.replace(/['"]/g, '').toLowerCase();
    if (ta === 'left') ai = 'flex-start';
    else if (ta === 'center') ai = 'center';
    else if (ta === 'right') ai = 'flex-end';

    const textWrap = root.querySelector('.text-wrap');
    if (textWrap) {
      textWrap.style.setProperty('--jc', jc);
      textWrap.style.setProperty('--ai', ai);
      textWrap.style.setProperty('text-align', ta);
    }
  }

  function runAdaptiveSizer(root, mediaEl) {
    if (!root || !mediaEl) return;

    const computed = getComputedStyle(root);
    const pagePaddingX = parseFloat(computed.getPropertyValue('--page-padding-x')) || 20;
    const maxHeightRaw = computed.getPropertyValue('--max-left-height') || '80vh';

    function toPx(value) {
      value = (value || '').trim();
      if (!value) return Math.round(window.innerHeight * 0.8);
      if (value.endsWith('vh')) {
        const vh = parseFloat(value);
        return Math.round(window.innerHeight * (vh / 100));
      } else if (value.endsWith('px')) {
        return Math.round(parseFloat(value));
      } else {
        return Math.round(parseFloat(value) || Math.round(window.innerHeight * 0.8));
      }
    }
    const maxAllowedHeight = toPx(maxHeightRaw);

    function adjustForDimensions(naturalWidth, naturalHeight) {
      if (!naturalWidth || !naturalHeight) return;
      const rootRect = root.getBoundingClientRect();
      const usableWidth = Math.max(0, rootRect.width - (pagePaddingX * 2));
      const leftWidth = Math.round(usableWidth / 2);
      const idealHeight = Math.round(leftWidth * (naturalHeight / naturalWidth));
      const leftPanel = root.querySelector('.civt-left');
      if (!leftPanel) return;

      const child = mediaEl.querySelector('img,video');

      if (idealHeight <= maxAllowedHeight) {
        leftPanel.style.height = idealHeight + 'px';
        if (child) {
          child.style.objectFit = 'cover';
          child.style.objectPosition = 'top center';
        }
      } else {
        leftPanel.style.height = maxAllowedHeight + 'px';
        if (child) {
          child.style.objectFit = 'contain';
          child.style.objectPosition = 'top center';
        }
      }
    }

    function handleLoadedMedia(el) {
      if (!el) return;
      if (el.tagName === 'IMG') {
        if (el.complete) adjustForDimensions(el.naturalWidth, el.naturalHeight);
        else el.addEventListener('load', function () { adjustForDimensions(el.naturalWidth, el.naturalHeight); });
      } else if (el.tagName === 'VIDEO') {
        if (el.readyState >= 1) adjustForDimensions(el.videoWidth, el.videoHeight);
        else el.addEventListener('loadedmetadata', function () { adjustForDimensions(el.videoWidth, el.videoHeight); });
      }
    }

    const childNow = mediaEl.querySelector('img, video');
    if (childNow) handleLoadedMedia(childNow);
    else {
      const observer = new MutationObserver(function (mutations) {
        for (const m of mutations) {
          for (const n of m.addedNodes) {
            if (n && (n.tagName === 'IMG' || n.tagName === 'VIDEO')) {
              handleLoadedMedia(n);
              observer.disconnect();
              return;
            }
          }
        }
      });
      observer.observe(mediaEl, { childList: true });
    }

    let t;
    window.addEventListener('resize', function () {
      clearTimeout(t);
      t = setTimeout(function () {
        const childAgain = mediaEl.querySelector('img,video');
        if (childAgain) handleLoadedMedia(childAgain);
      }, 120);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initCIVT('civt-root'); });
  } else {
    initCIVT('civt-root');
  }
})();
